import React, { useEffect, useState, useMemo, useContext } from "react";
import { AuthContext } from "../../../Contexts/AuthContext";
import { Link } from "react-router-dom";
import { getUsers, deleteUser, addUser, updateUserAdmin } from "../../../api/usersApi";
import './styles/usersList.css';
import swal from "sweetalert";
import { useTranslation } from "react-i18next";
import { FiUsers, FiUserPlus, FiMail, FiEdit2, FiTrash2, FiLoader, FiShield, FiSearch } from "react-icons/fi";
import { GiFarmer } from "react-icons/gi";

export function UsersList() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [form, setForm] = useState({ nom: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    getUsers()
      .then(data => setUsers(data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(u =>
      u.nom?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q)
    );
  }, [users, search]);

  const adminCount = useMemo(() => users.filter(u => u.role === 'admin').length, [users]);
  const eleveurCount = useMemo(() => users.filter(u => u.role === 'eleveur').length, [users]);

  async function handleDelete(id) {
    swal({
      title: t("usersList.swal.confirmTitle"),
      text: t("usersList.swal.confirmText"),
      icon: "warning",
      buttons: [t("usersList.swal.no"), t("usersList.swal.yes")],
      dangerMode: true,
    }).then(async (willContinue) => {
      if (willContinue) {
        try {
          await deleteUser(id);
          setUsers(us => us.filter(u => u.id !== id));
          swal(t("usersList.swal.success"), "", "success");
        } catch (e) {
          console.error(e);
          swal(t("usersList.swal.errorTitle"), t("usersList.swal.errorText"), "error");
        }
      } else {
        swal(t("usersList.swal.cancelled"));
      }
    });
  }

  function openCreateModal() {
    setIsEdit(false);
    setCurrentUserId(null);
    setForm({ nom: "", email: "", password: "" });
    setIsModalOpen(true);
  }

  function openEditModal(user) {
    setIsEdit(true);
    setCurrentUserId(user.id);
    setForm({ nom: user.nom || "", email: user.email || "", password: "" });
    setIsModalOpen(true);
  }

  function closeModal() {
    if (submitting) return;
    setIsModalOpen(false);
    setCurrentUserId(null);
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (isEdit && currentUserId) {
        await updateUserAdmin(currentUserId, form);
      } else {
        await addUser(form);
      }

      const refreshed = await getUsers();
      setUsers(refreshed);
      closeModal();
    } catch (err) {
      console.error(err);
      swal(t("addEditUser.errors.title"), t("addEditUser.errors.invalidInput"), "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="ul-page">
      {/* Header */}
      <div className="ul-header">
        <div className="ul-header-content">
          <div className="ul-header-icon"><FiUsers /></div>
          <div>
            <h1 className="ul-title">{t("usersList.title")}</h1>
            <p className="ul-subtitle">{t("usersList.subtitle") || "Manage team members and roles"}</p>
          </div>
        </div>
        <button type="button" className="ul-add-btn" onClick={openCreateModal}>
          <FiUserPlus /> {t("usersList.addUser")}
        </button>
      </div>

      {/* Stats */}
      {!loading && (
        <div className="ul-stats">
          <div className="ul-stat">
            <span className="ul-stat-icon ul-stat-total"><FiUsers /></span>
            <div>
              <span className="ul-stat-value">{users.length}</span>
              <span className="ul-stat-label">{t("usersList.stats.total")}</span>
            </div>
          </div>
          <div className="ul-stat">
            <span className="ul-stat-icon ul-stat-admin"><FiShield /></span>
            <div>
              <span className="ul-stat-value">{adminCount}</span>
              <span className="ul-stat-label">{t("usersList.stats.admins")}</span>
            </div>
          </div>
          <div className="ul-stat">
            <span className="ul-stat-icon ul-stat-eleveur"><GiFarmer /></span>
            <div>
              <span className="ul-stat-value">{eleveurCount}</span>
              <span className="ul-stat-label">{t("usersList.fields.eleveur") || "Éleveurs"}</span>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      {!loading && users.length > 0 && (
        <div className="ul-search-wrap">
          <FiSearch className="ul-search-icon" />
          <input
            type="text"
            className="ul-search"
            placeholder={t("usersList.searchPlaceholder") || "Search by name, email or role…"}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="ul-loading">
          <FiLoader className="ul-loading-icon" />
          <span>{t("usersList.loading")}</span>
        </div>
      )}

      {/* Empty */}
      {!loading && users.length === 0 && (
        <div className="ul-empty">
          <FiUsers className="ul-empty-icon" />
          <h3>{t("usersList.empty.title")}</h3>
          <p>{t("usersList.empty.subtitle")}</p>
        </div>
      )}

      {/* Users Grid */}
      {!loading && filtered.length > 0 && (
        <div className="ul-grid">
          {filtered.map(u => (
            <div key={u.id} className="ul-card">
              <div className="ul-card-top">
                <div className="ul-avatar">{u.nom?.charAt(0)?.toUpperCase() || "?"}</div>
                <div className="ul-user-info">
                  <h3 className="ul-name">{u.nom}</h3>
                  <p className="ul-email"><FiMail /> {u.email || t("usersList.fields.noEmail")}</p>
                </div>
                <span className={`ul-role ${u.role === 'admin' ? 'ul-role-admin' : 'ul-role-eleveur'}`}>
                  {u.role === 'admin' ? <FiShield /> : <GiFarmer />}
                  {u.role || t("usersList.fields.noRole")}
                </span>
              </div>
              <div className="ul-card-actions">
                <button
                  type="button"
                  className="ul-btn-edit"
                  title={t("usersList.actions.edit")}
                  onClick={() => openEditModal(u)}
                >
                  <FiEdit2 /> {t("usersList.actions.edit")}
                </button>
                <button onClick={() => handleDelete(u.id)} className="ul-btn-delete" title={t("usersList.actions.delete")}>
                  <FiTrash2 /> {t("usersList.actions.delete")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results */}
      {!loading && users.length > 0 && filtered.length === 0 && (
        <div className="ul-no-results">
          <FiSearch className="ul-empty-icon" />
          <p>{t("usersList.noResults") || "No matching users found"}</p>
        </div>
      )}

      {isModalOpen && (
        <div className="ul-modal-overlay" onClick={closeModal}>
          <div className="ul-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ul-modal-header">
              <h2 className="ul-modal-title">
                {isEdit ? t("addEditUser.titleEdit") : t("addEditUser.titleAdd")}
              </h2>
              <button type="button" className="ul-modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="ul-modal-form">
              <div className="ul-modal-field">
                <label className="ul-modal-label">
                  {t("addEditUser.fields.name")}
                </label>
                <input
                  type="text"
                  name="nom"
                  className="ul-modal-input"
                  placeholder={t("addEditUser.placeholders.name") || "Full name"}
                  value={form.nom}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="ul-modal-field">
                <label className="ul-modal-label">
                  {t("addEditUser.fields.email")}
                </label>
                <input
                  type="email"
                  name="email"
                  className="ul-modal-input"
                  placeholder={t("addEditUser.placeholders.email") || "name@example.com"}
                  value={form.email}
                  onChange={handleChange}
                  required
                  disabled={isEdit && user && user.role === 'admin' && String(user.id) === String(currentUserId)}
                />
              </div>
              <div className="ul-modal-field">
                <label className="ul-modal-label">
                  {t("addEditUser.fields.password")}
                </label>
                <input
                  type="password"
                  name="password"
                  className="ul-modal-input"
                  placeholder={isEdit ? "••••••••" : (t("addEditUser.placeholders.password") || "Password")}
                  value={form.password}
                  onChange={handleChange}
                />
              </div>
              <div className="ul-modal-actions">
                <button
                  type="button"
                  className="ul-btn-secondary"
                  onClick={closeModal}
                  disabled={submitting}
                >
                  {t("addEditUser.buttons.cancel") || "Cancel"}
                </button>
                <button type="submit" className="ul-btn-primary" disabled={submitting}>
                  {submitting
                    ? t("addEditUser.buttons.saving") || "Saving…"
                    : (isEdit ? t("addEditUser.buttons.save") : t("addEditUser.buttons.create"))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
